// frontend/src/pages/FinancialImpactDashboardPage.tsx
// Fixed: Corrected API URL (removed double /api/), improved portfolio loading

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FinancialImpactDashboard from '../components/Portfolio/FinancialImpactDashboard';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

interface PortfolioSummary {
  id: string;
  name: string;
  item_count?: number;
  total_value?: number;
  avg_risk?: number;
  items?: any[];
}

interface PortfolioData {
  id: string;
  name: string;
  items: any[];
  total_value?: number;
  avg_risk?: number;
}

const FinancialImpactDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<PortfolioSummary[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [portfolio, setPortfolio] = useState<PortfolioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch list of portfolios on mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        setLoading(true);
        setError(null);

        const userId = user.id || 'demo';
        console.log('FinancialImpact: Fetching portfolios for userId:', userId);

        // Fixed: Use correct API URL pattern (no double /api/)
        const response = await axios.get(`${API_URL}/portfolios`, {
          params: { userId },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.length > 0) {
          setPortfolios(response.data);
          
          // Auto-select the first portfolio or the one with most items
          const defaultPortfolio = response.data.reduce((prev: PortfolioSummary, current: PortfolioSummary) => {
            const prevCount = prev.item_count || prev.items?.length || 0;
            const currentCount = current.item_count || current.items?.length || 0;
            return currentCount > prevCount ? current : prev;
          });
          
          setSelectedPortfolioId(defaultPortfolio.id);
        } else {
          setError('No portfolios found. Please create a portfolio first.');
        }
      } catch (err: any) {
        console.error('Error fetching portfolios:', err);
        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setError('Failed to load portfolios. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, [navigate, token]);

  // Fetch portfolio analysis when selection changes
  useEffect(() => {
    if (!selectedPortfolioId) return;

    const fetchPortfolioAnalysis = async () => {
      try {
        setLoadingAnalysis(true);
        setError(null);

        const response = await axios.get(
          `${API_URL}/portfolios/${selectedPortfolioId}/analysis`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          setPortfolio({
            id: selectedPortfolioId,
            name: response.data.name || portfolios.find(p => p.id === selectedPortfolioId)?.name || 'Portfolio',
            items: response.data.items || [],
            total_value: response.data.total_value,
            avg_risk: response.data.avg_risk,
          });
        }
      } catch (err: any) {
        console.error('Error fetching portfolio analysis:', err);
        setError('Failed to load portfolio analysis. Please try again.');
      } finally {
        setLoadingAnalysis(false);
      }
    };

    fetchPortfolioAnalysis();
  }, [selectedPortfolioId, token, portfolios]);

  const handlePortfolioChange = (event: any) => {
    setSelectedPortfolioId(event.target.value);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={48} sx={{ color: '#2563EB', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#6B7280' }}>
              Loading portfolios...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error && portfolios.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>
        
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          {error}
        </Alert>
        
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#111827', mb: 1 }}>
            Unable to Load Portfolios
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            {error}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Go to Dashboard
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/portfolio')}
            >
              Manage Portfolios
            </Button>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>
            Financial Impact Analysis
          </Typography>
        </Box>
        
        {/* Portfolio Selector */}
        {portfolios.length > 0 && (
          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel>Select Portfolio</InputLabel>
            <Select
              value={selectedPortfolioId}
              onChange={handlePortfolioChange}
              label="Select Portfolio"
            >
              {portfolios.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} ({p.item_count || p.items?.length || 0} locations)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading Analysis */}
      {loadingAnalysis && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress size={48} sx={{ color: '#2563EB', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#6B7280' }}>
              Loading portfolio analysis...
            </Typography>
          </Box>
        </Box>
      )}

      {/* No Portfolio Selected */}
      {!loadingAnalysis && !portfolio && portfolios.length > 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#111827', mb: 1 }}>
            Select a Portfolio
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Choose a portfolio from the dropdown above to view financial impact analysis
          </Typography>
        </Paper>
      )}

      {/* No Portfolios Exist */}
      {!loadingAnalysis && portfolios.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#111827', mb: 1 }}>
            No Portfolios Found
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            Create your first portfolio to start analyzing financial impacts of climate risks
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/portfolio')}
          >
            Create Portfolio
          </Button>
        </Paper>
      )}

      {/* Financial Impact Dashboard */}
      {!loadingAnalysis && portfolio && portfolio.items && portfolio.items.length > 0 && (
        <FinancialImpactDashboard portfolio={portfolio} />
      )}

      {/* Portfolio Has No Items */}
      {!loadingAnalysis && portfolio && (!portfolio.items || portfolio.items.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#111827', mb: 1 }}>
            Portfolio Has No Locations
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            This portfolio doesn't have any locations yet. Add locations to see financial impact analysis.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/portfolio')}
          >
            Manage Portfolio
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default FinancialImpactDashboardPage;